import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Lock } from "lucide-react";
import Seo from "../components/Seo";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetPassword() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(token ? "" : "This link is missing its token — use the button in your email.");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/set-password`, { token, password });
      sessionStorage.setItem("ig_admin_token", r.data.token);
      toast({ title: "Password set", description: "You're signed in — welcome to the dashboard." });
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't set your password. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Seo title="Set Password | Boston Iron Grit Admin" description="Set your dashboard password." path="/admin/set-password" noindex />
      <section className="min-h-[80vh] bg-[#0A0A0B] flex items-center justify-center px-6">
        <div className="w-full max-w-sm bg-[#121214] border border-white/10 p-9">
          <div className="w-12 h-12 bg-[#9D0423]/12 border border-[#9D0423]/30 flex items-center justify-center">
            <Lock size={20} className="text-[#9D0423]" />
          </div>
          <h1 className="mt-6 font-heading font-extrabold uppercase text-2xl text-white tracking-tight">Set Your Password</h1>
          <p className="mt-2 text-[13px] text-neutral-500 leading-relaxed">
            Choose a password (8+ characters) for your Boston Iron Grit dashboard login.
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password"
              aria-label="New password"
              autoComplete="new-password"
              data-testid="set-password-input"
              className="w-full bg-[#0D0D0F] border border-white/10 focus:border-[#9D0423] focus:outline-none focus:ring-1 focus:ring-[#9D0423]/40 text-white placeholder:text-neutral-600 px-4 py-3.5 text-sm transition-colors"
            />
            <input
              type="password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm password"
              aria-label="Confirm password"
              autoComplete="new-password"
              data-testid="set-password-confirm"
              className="w-full bg-[#0D0D0F] border border-white/10 focus:border-[#9D0423] focus:outline-none focus:ring-1 focus:ring-[#9D0423]/40 text-white placeholder:text-neutral-600 px-4 py-3.5 text-sm transition-colors"
            />
            {error && <p className="text-[12px] text-[#E56A79]" data-testid="set-password-error">{error}</p>}
            <button
              type="submit"
              disabled={saving || !token}
              data-testid="set-password-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#9D0423] hover:bg-[#7E031C] disabled:opacity-60 text-white font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-6 py-3.5 transition-colors"
            >
              {saving ? "Saving…" : "Set Password & Sign In"}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
